import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AvatarSize, AvatarState } from '@rivethealth/data/options';
import { Permission } from '@rivethealth/data/user-account';
import _, { memoize, sortBy } from 'lodash';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { loadMap, success } from '../common/helpers/loadable';
import { replayLast } from '../common/rx/util';
import { API_URL } from './common';
import { ChangeUser, SavedUser, User, UserService } from './user.service';

@Injectable({ providedIn: 'root' })
export class AvatarService {
  constructor(
    @Inject(API_URL) private readonly apiUrl: string,
    private readonly http: HttpClient,
    private readonly userService: UserService,
  ) {}

  cacheBust = new BehaviorSubject<number>(0);
  cacheInitial = new BehaviorSubject<number>(0);

  getAvatar = this.cacheBust.pipe(
    distinctUntilChanged(),
    map(
      () =>
        memoize((userId: number, size: number): Observable<Blob> => {
          return this.http
            .request('GET', `${this.apiUrl}/users/${userId}/avatar`, {
              params: { size: size.toString() },
              responseType: 'blob',
            })
            .pipe(shareReplay(1));
        }),
      (userId: number, size: number) => `${userId}|${size}`,
    ),
    shareReplay(1),
  );

  getInitials = this.cacheInitial.pipe(
    distinctUntilChanged(),
    map(
      () =>
        memoize((userId: number) =>
          this.userService.getUser(userId).pipe(shareReplay(1)),
        ),
      (userId: number) => `${userId}`,
    ),
    shareReplay(1),
  );

  setAvatar(userId: number, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.http
      .put(`${this.apiUrl}/users/${userId}/avatar`, formData)
      .pipe(
        withLatestFrom(this.cacheBust),
        tap(([_, num]) => {
          this.cacheBust.next(num + 1);
        }),
        map(([obj, _]) => obj),
      );
  }

  modifyUserAndAvatar(accountId: number, userId: number, user: ChangeUser) {
    return this.http
      .put(`${this.apiUrl}/accounts/${accountId}/users/${userId}`, user)
      .pipe(
        withLatestFrom(this.cacheInitial),
        tap(([_, num]) => {
          this.cacheInitial.next(num + 1);
        }),
        map(([obj, _]) => obj),
      );
  }

  getUser = memoize((userId: number) =>
    this.userService.getUser(userId).pipe(shareReplay(1)),
  );

  getAvatarState(
    userId: number | null,
    size: AvatarSize = AvatarSize.SMALL,
    email?: string | null,
    name?: string,
    typeUser?: User & { type: 'system' | 'user' },
  ): Observable<AvatarState> {
    const user = of(userId).pipe(
      filter(i => i != null),
      switchMap(i => {
        return typeUser ? of(typeUser) : this.getUser(i!);
      }),
      replayLast(),
    );
    const uploadedAvatarUrl =
      null; /*combineLatest([of(userId), this.getAvatar]).pipe(
      filter(([i, _]) => i != null),
      switchMap(([userId, getAvatar]) => getAvatar(userId!, size * 2))
    );*/
    const imageUrl = null; /*uploadedAvatarUrl.pipe(
      filter(blob => blob !== null),
      map(blob => URL.createObjectURL(blob)),
      map(url => `url(${url})`),
      startWith(null),
    );*/
    const userTypeClass = user.pipe(
      map(u => (u.type == 'system' ? 'riveter' : '')),
      startWith(''),
    );
    const actualName = combineLatest([of(userId), this.getInitials]).pipe(
      filter(([i, _]) => i !== null),
      switchMap(([userId, getInitials]) =>
        typeUser ? of(typeUser) : getInitials(userId!),
      ),
      map(user => user.name),
      startWith(null),
    );

    return combineLatest([actualName, userTypeClass]).pipe(
      map(([name, userTypeClass]) => ({
        name: name!,
        userTypeClass,
        imageUrl: null,
        size,
      })),
      replayLast(),
    );
  }

  getUserAvatarsByAccount(
    accountId: number,
    permissionRequired: Permission[] = [],
    size: AvatarSize = AvatarSize.SMALL,
  ): Observable<{ user: SavedUser; avatarState: AvatarState }[]> {
    const users = of(accountId).pipe(
      loadMap(accountId =>
        this.userService.getUsers(accountId, false, permissionRequired),
      ),
      success(),
      map(users => sortBy(users, u => u.name.toLocaleLowerCase())),
      replayLast(),
    );
    return users.pipe(
      switchMap(users =>
        combineLatest(
          users.map(user =>
            this.getAvatarState(
              user.id,
              size,
              user.email,
              user.name,
              user,
            ).pipe(map(avatarState => ({ user, avatarState }))),
          ),
        ),
      ),
      startWith([]),
      shareReplay(1),
    );
  }

  getUserAvatarOptionsByAccount(accountId: number) {
    const userAvatars = this.getUserAvatarsByAccount(accountId);
    return userAvatars.pipe(
      map(usersPlusAvatars =>
        _.sortBy(usersPlusAvatars, userPlus =>
          userPlus.user.name.toLocaleLowerCase(),
        ),
      ),
      map(usersPlusAvatars =>
        usersPlusAvatars.map(i => ({
          id: i.user.id.toString(),
          title: i.user.name,
          subtitle: null,
          avatarState: i.avatarState,
        })),
      ),
    );
  }

  // Gets a set of user avatars associated with a set of user ids.
  getAvatarsForSpecificUsers(
    userIds: number[] = [],
  ): Observable<{ userId: number; avatarState: AvatarState }[]> {
    return of(userIds).pipe(
      switchMap(ids =>
        combineLatest(
          ids.map(id =>
            this.getAvatarState(id, AvatarSize.SMALL).pipe(
              map(avatarState => ({ userId: id, avatarState })),
            ),
          ),
        ),
      ),
      startWith([]),
      shareReplay(1),
    );
  }

  // Instead of doing whatever madness is happending above, just
  // get all of the users and return their avatar states directly.
  getUserAvatarsByAccountBatched(
    accountId: number,
    size: AvatarSize = AvatarSize.SMALL,
  ): Observable<{ user: SavedUser; avatarState: AvatarState }[]> {
    return this.userService.getUsers(accountId, false).pipe(
      map(users =>
        users.map(u => {
          return {
            user: u,
            avatarState: {
              name: u.name,
              userTypeClass: u.type == 'system' ? 'riveter' : '',
              imageUrl: null,
              size,
            },
          };
        }),
      ),
    );
  }
}
